import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Add, ArrowBack, SportsEsports, ViewModule } from '@mui/icons-material'
import { Box, Button, Card, Stack, TableContainer, Typography } from '@mui/material'

import { FtueVideo } from '../../../api/core'
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import { useAppDispatch } from '../../../hooks/storeHooks'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { invalidateFtueVideoLists, useDeleteFtueVideoMutation, useGetFtueVideosByGameQuery, useGetFtueVideosQuery } from '../../api/ftue-videos'
import { useGetInternalGameQuery } from '../../api/internal-games'
import { InternalGameSelectorDialog } from '../../components/InternalGameSelectorDialog/InternalGameSelectorDialog'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../components/RowActions/RowActions'
import { FtueGameHeader } from '../../features/ftue-videos/components/FtueGameHeader/FtueGameHeader'
import { FtueVideoCard } from '../../features/ftue-videos/components/FtueVideoCard/FtueVideoCard'
import { filterFtueVideos, formatReleaseDate, getFtueRowThumbnail, sortFtueVideos } from '../../features/ftue-videos/helpers/ftueVideoHelpers'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type CustomProps = { onOpenGame: (video: FtueVideo) => void; onEdit: (video: FtueVideo) => void; onDelete: (video: FtueVideo) => void }

export const ftueVideosPath = '/internal/ftue-videos'
export const ftueVideosGamePath = (gameId: string) => `${ftueVideosPath}?gameId=${encodeURIComponent(gameId)}`
export const ftueVideoAttachPath = (gameId: string) => `${ftueVideosPath}/attach?gameId=${encodeURIComponent(gameId)}`
export const ftueVideoEditPath = (id: string) => `${ftueVideosPath}/${id}/edit`

export const FtueVideosPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-ftue-videos'))
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const gameId = searchParams.get('gameId') ?? ''
  const gameQuery = useGetInternalGameQuery(gameId, { skip: !gameId })
  const gameVideosQuery = useGetFtueVideosByGameQuery(gameId, { skip: !gameId })
  const allVideosQuery = useGetFtueVideosQuery(undefined, { skip: !!gameId })
  const [deleteVideo] = useDeleteFtueVideoMutation()
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<FtueVideo>>()

  const shownQuery = gameId ? gameVideosQuery : allVideosQuery
  const sortedVideos = useMemo(() => sortFtueVideos(shownQuery.data ?? []), [shownQuery.data])
  const rows = useMemo(() => (gameId ? sortedVideos : filterFtueVideos(sortedVideos, search)), [gameId, sortedVideos, search])

  const confirmDelete = useCallback(
    (video: FtueVideo) =>
      setConfirmDialogData({
        title: t('internal-analysts:ftue_videos_delete'),
        confirmText: t('internal-analysts:ftue_videos_delete_confirm', { name: video.names?.en || '---' }),
        actionText: t('internal-common:delete'),
        destructiveAction: true,
        data: video,
      }),
    [t]
  )

  const customProps = useMemo<CustomProps>(
    () => ({
      onOpenGame: (video) => setSearchParams({ gameId: video.gameId }),
      onEdit: (video) => navigate(ftueVideoEditPath(video.id)),
      onDelete: confirmDelete,
    }),
    [confirmDelete, navigate, setSearchParams]
  )

  const tableColumns: GRTableColumn<FtueVideo, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:ftue_videos_thumbnail'),
        accessor: ({ row }) => {
          const thumbnail = getFtueRowThumbnail(row)
          return thumbnail ? (
            <Box component="img" src={thumbnail.link} alt="" loading="lazy" sx={{ display: 'block', width: (theme) => theme.spacing(12) }} />
          ) : (
            <Typography color="text.secondary">---</Typography>
          )
        },
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 120, minWidth: 120 } },
      },
      {
        labelAccessor: t('internal-analysts:ftue_videos_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.names?.en || '---'}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.names?.en?.toLowerCase() ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-analysts:ftue_videos_version'),
        accessor: ({ row }) => row.gameVersion || '---',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 120 } },
      },
      {
        labelAccessor: t('internal-analysts:ftue_videos_market'),
        accessor: ({ row }) => row.marketIso || '---',
        sortable: true,
        sortAccessor: ({ row }) => row.marketIso?.toLowerCase() ?? '',
        headerCellProps: { sx: { width: 90 } },
      },
      {
        labelAccessor: t('internal-analysts:ftue_videos_release_date'),
        accessor: ({ row }) => formatReleaseDate(row.gameVersionReleaseDate),
        sortable: true,
        sortAccessor: ({ row }) => row.gameVersionReleaseDate ?? 0,
        headerCellProps: { sx: { width: 120 } },
      },
      {
        labelAccessor: t('internal-common:ftue_chapters'),
        accessor: ({ row }) => row.chapters?.length ?? 0,
        sortable: true,
        sortAccessor: ({ row }) => row.chapters?.length ?? 0,
        headerCellProps: { sx: { width: 100 } },
      },
      {
        labelAccessor: t('internal-analysts:ftue_videos_attached'),
        accessor: ({ row }) => formatReleaseDate(row.createdAt),
        sortable: true,
        sortAccessor: ({ row }) => row.createdAt,
        headerCellProps: { sx: { width: 120 } },
      },
      rowActionsColumn<FtueVideo, CustomProps>({
        width: 150,
        actions: ({ row, customTableProps }) => {
          const name = row.names?.en || '---'
          return [
            {
              icon: <ViewModule />,
              label: t('internal-analysts:ftue_videos_open_game'),
              ariaLabel: t('internal-analysts:ftue_videos_open_game_named', { name }),
              onClick: () => customTableProps?.onOpenGame(row),
            },
            {
              type: 'edit',
              label: t('internal-analysts:ftue_videos_edit'),
              ariaLabel: t('internal-analysts:ftue_videos_edit_named', { name }),
              onClick: () => customTableProps?.onEdit(row),
            },
            {
              type: 'delete',
              label: t('internal-analysts:ftue_videos_delete'),
              ariaLabel: t('internal-analysts:ftue_videos_delete_named', { name }),
              onClick: () => customTableProps?.onDelete(row),
            },
          ]
        },
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const { refetch } = shownQuery
  const handleConfirm = useCallback(
    async (confirmed?: ConfirmDialogData<FtueVideo>) => {
      setConfirmDialogData(undefined)
      const video = confirmed?.data
      if (!video) return
      const name = video.names?.en || '---'
      try {
        await deleteVideo({ id: video.id, gameId: video.gameId }).unwrap()
        const remaining = await refetch().unwrap()
        if (remaining.some((listed) => listed.id === video.id)) {
          notifyError(t('internal-analysts:ftue_videos_not_deleted', { name }))
          return
        }
        notifySuccess(t('internal-analysts:ftue_videos_deleted', { name }))
      } catch {
        notifyError(t('internal-analysts:ftue_videos_delete_failed', { name }))
      } finally {
        dispatch(invalidateFtueVideoLists(gameId ? { all: true } : { gameId: video.gameId }))
      }
    },
    [deleteVideo, dispatch, gameId, notifyError, notifySuccess, refetch, t]
  )

  const selectGameButton = (
    <Button variant="outlined" startIcon={<SportsEsports />} onClick={() => setSelectorOpen(true)}>
      {t('internal-analysts:ftue_videos_select_game')}
    </Button>
  )

  const actions = (
    <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
      {selectGameButton}
      <Button variant="contained" startIcon={<Add />} disabled={!gameId} onClick={() => navigate(ftueVideoAttachPath(gameId))}>
        {t('internal-analysts:ftue_videos_attach')}
      </Button>
    </Stack>
  )

  return (
    <Box>
      {gameId && (
        <Stack direction="row" alignItems="center" mb={1} spacing={2}>
          <Button startIcon={<ArrowBack />} onClick={() => setSearchParams({})}>
            {t('internal-analysts:ftue_videos_all')}
          </Button>
          {actions}
        </Stack>
      )}
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t(gameId ? 'internal-analysts:ftue_videos_game_intro' : 'internal-analysts:ftue_videos_intro')}
      </Typography>

      {gameId ? (
        <Stack spacing={2}>
          {gameQuery.isError ? (
            <Typography color="error.main">{t('internal-analysts:ftue_videos_game_load_failed')}</Typography>
          ) : (
            <FtueGameHeader game={gameQuery.data} isLoading={gameQuery.isLoading} />
          )}
          <QueryStateBoundary
            isLoading={gameVideosQuery.isLoading}
            isError={gameVideosQuery.isError}
            errorText={t('internal-analysts:ftue_videos_load_failed')}
            skeletonHeight={240}
          >
            {rows.length === 0 ? (
              <Typography color="text.secondary">{t('internal-analysts:ftue_videos_none_for_game')}</Typography>
            ) : (
              <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))" gap={2}>
                {rows.map((video) => (
                  <FtueVideoCard key={video.id} video={video} onEdit={customProps.onEdit} onDelete={customProps.onDelete} />
                ))}
              </Box>
            )}
          </QueryStateBoundary>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <InternalListToolbar
            search={{ value: search, onChange: setSearch, placeholder: t('internal-analysts:ftue_videos_search'), minWidth: 360 }}
            actions={selectGameButton}
            add={{ label: t('internal-analysts:ftue_videos_attach'), onClick: () => navigate(ftueVideoAttachPath(gameId)), disabled: !gameId }}
          >
            {allVideosQuery.data && (
              <Typography variant="body2" color="text.secondary">
                {t('internal-analysts:ftue_videos_count', { count: rows.length, total: sortedVideos.length })}
              </Typography>
            )}
          </InternalListToolbar>
          <QueryStateBoundary isLoading={allVideosQuery.isLoading} isError={allVideosQuery.isError} errorText={t('internal-analysts:ftue_videos_load_failed')}>
            <TableContainer component={Card}>
              <GRTable
                rows={rows}
                columns={columns}
                onColumnsUpdated={setColumns}
                rowIdKey="id"
                customProps={customProps}
                noRowsLabel={t('internal-analysts:ftue_videos_none')}
                striped
                hoverable
              />
            </TableContainer>
          </QueryStateBoundary>
        </Stack>
      )}

      <InternalGameSelectorDialog
        open={selectorOpen}
        title={t('internal-analysts:ftue_videos_select_game')}
        maxSelected={1}
        onClose={() => setSelectorOpen(false)}
        onDone={(games) => {
          setSelectorOpen(false)
          if (games[0]) setSearchParams({ gameId: games[0].id })
        }}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Box>
  )
}
